/**
 * The page's side of extraction: hand over a file, get text back.
 *
 * The PDF is read with `arrayBuffer()` and transferred into the worker, so the bytes are moved
 * rather than copied and a 40 MB book does not exist twice. Nothing here uploads anything —
 * `File` → `ArrayBuffer` → worker is the whole path, and there is no fetch in it. PLAN.md §6.11.
 */
import type { ExtractMessage } from '../workers/pdf-extract.worker';
import type { PlacedLine } from './pdf-layout';

export interface Extraction {
  pages: string[];
  text: string;
  pageCount: number;
  /** How many pages were read as two columns, for the review surface to report honestly. */
  twoColumnPages: number;
  furnitureDropped: number;
  /** Line `i` of `text`, and the box it was printed in. What click regions are derived from. */
  placed: PlacedLine[];
  pageSizes: { width: number; height: number }[];
}

export interface ExtractOptions {
  onProgress?: (page: number, pages: number) => void;
  signal?: AbortSignal;
}

export function extractPdf(file: Blob, options: ExtractOptions = {}): Promise<Extraction> {
  return new Promise<Extraction>((resolve, reject) => {
    const worker = new Worker(new URL('../workers/pdf-extract.worker.ts', import.meta.url), {
      type: 'module',
    });

    const finish = (settle: () => void) => {
      worker.terminate();
      options.signal?.removeEventListener('abort', onAbort);
      settle();
    };
    function onAbort() { finish(() => reject(new Error('Extraction cancelled'))); }
    options.signal?.addEventListener('abort', onAbort);

    worker.onmessage = (event: MessageEvent<ExtractMessage>) => {
      const message = event.data;
      /**
       * pdf.js's worker build announces itself.
       *
       * Importing `pdf.worker.min.mjs` inside a worker makes it initialise as pdf.js's OWN
       * worker as a side effect of module evaluation, and the first thing it does is post
       * `{ sourceName: 'worker', action: 'ready' }` to whoever opened it — before the page has
       * sent anything. That message has no `type`, so it fell through to the error branch as
       * `new Error(undefined)`: extraction failed instantly, every time, with a completely
       * blank message. Anything without one of our three tags is not ours.
       */
      if (!message || typeof message.type !== 'string') return;
      if (message.type === 'progress') options.onProgress?.(message.page, message.pages);
      else if (message.type === 'done') finish(() => resolve({
        pages: message.pages,
        text: message.text,
        pageCount: message.pageCount,
        twoColumnPages: message.twoColumnPages,
        furnitureDropped: message.furnitureDropped,
        placed: message.placed,
        pageSizes: message.pageSizes,
      }));
      else finish(() => reject(new Error(message.message)));
    };
    // A worker that dies takes the promise with it; without this the review surface waits for a
    // result that can never arrive and says nothing at all.
    worker.onerror = event => finish(() => reject(new Error(event.message || 'The PDF reader failed to start')));

    void file.arrayBuffer().then(
      data => worker.postMessage({ data }, [data]),
      error => finish(() => reject(error instanceof Error ? error : new Error(String(error)))),
    );
  });
}
